import { useState } from 'react';
import { useApiClient } from '@/lib/api/hooks';
import type { paths } from '@/lib/api/types.gen';
import type { ApiErrorBody, QuoteError } from './_shared';

type AssistBody = NonNullable<
  paths['/calc/assist']['post']['requestBody']
>['content']['application/json'];
type AssistInput = AssistBody['input'];

export type CalcAssistQuestion = 'explain_floor' | 'how_low' | 'why_margin';

export type CalcAssistPatch = {
  sxl?: number;
  xxl?: number;
  xxxl?: number;
  xxxxl?: number;
  xxxxxl?: number;
  frontEnabled?: boolean;
  frontColors?: number;
  backEnabled?: boolean;
  backColors?: number;
  sleeveEnabled?: boolean;
  sleeveColors?: number;
  garmentSource?: 'customer_provides' | 'we_provide' | 'we_provide_by_style';
  blankCostEach?: number;
  garmentColor?: 'white' | 'colored';
  style?: string;
  colorName?: string;
  mode?: 'b2c' | 'b2b';
  discountPct?: number;
};

export type CalcAssistAction = { label: string; patch: CalcAssistPatch };

export type CalcAssistMetrics = {
  maxDiscountPct?: number;
  resultingMarginPct?: number;
  resultingPerUnitSell?: number;
  resultingOrderTotal?: number;
};

export type CalcAssistResult = {
  answer: string;
  actions?: CalcAssistAction[];
  metrics?: CalcAssistMetrics;
};

export type AssistState =
  | { status: 'idle' }
  | { status: 'loading'; question: CalcAssistQuestion }
  | { status: 'success'; question: CalcAssistQuestion; result: CalcAssistResult }
  | { status: 'error'; question: CalcAssistQuestion; error: QuoteError };

export function useCalcAssist() {
  const client = useApiClient();
  const [state, setState] = useState<AssistState>({ status: 'idle' });

  async function ask(
    question: CalcAssistQuestion,
    input: AssistInput,
    targetMarginPct?: number,
  ): Promise<CalcAssistResult | null> {
    setState({ status: 'loading', question });
    try {
      const { data, error, response } = await client.POST('/calc/assist', {
        body: { question, input, targetMarginPct },
      });
      if (error || !data) {
        const envelope = error as { error?: ApiErrorBody } | undefined;
        const quoteError: QuoteError = {
          status: response.status,
          body: envelope?.error ?? {
            type: 'unknown',
            title: 'Unknown error',
            detail: `HTTP ${response.status}`,
          },
        };
        setState({ status: 'error', question, error: quoteError });
        return null;
      }
      setState({ status: 'success', question, result: data.data });
      return data.data;
    } catch (err) {
      const quoteError: QuoteError = {
        status: 0,
        body: {
          type: 'network_error',
          title: 'Could not reach the API',
          detail: err instanceof Error ? err.message : 'Request failed before reaching the server.',
        },
      };
      setState({ status: 'error', question, error: quoteError });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle' });
  }

  return { state, ask, reset };
}
